import { RepresentationTypes, UpsertAmbassadorRequest } from '@amzn/red-velvet-api';

export type ListSplitter = 'newline' | 'comma' | RegExp | ((val: string) => string[]);

export interface ParseOptions {
  // You can key by either the original header ("Interest Areas")
  // or the camelCase header ("interestAreas")
  listFields?: Record<string, ListSplitter>;
}

export async function parseCsvFile(
  file: File,
  options: ParseOptions = {
    listFields: {
      whereDidYouHearAboutTheProgam: 'newline',
      interestAreas: 'newline',
      teamLocation: 'newline',
      representationType: 'comma',
    },
  }
): Promise<Record<string, string | string[]>[]> {
  const text = await file.text();
  return parseCsvText(text, options);
}

export function parseCsvText(
  input: string,
  options: ParseOptions = {}
): Record<string, string | string[]>[] {
  const rows = parseCsv(input);
  if (rows.length === 0) return [];

  const rawHeaders = rows[0].map((h) => h.trim());
  const camelHeaders = makeUnique(rawHeaders.map((h) => toCamelCase(h)));

  const records: Record<string, string | string[]>[] = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const obj: Record<string, string | string[]> = {};

    for (let i = 0; i < camelHeaders.length; i++) {
      const camelKey = camelHeaders[i];
      const val = String(row[i] ?? '');

      // if key name is in options.listFields, that column is an array of strings instead of just a string
      const splitter = options.listFields?.[camelKey];
      if (splitter) {
        obj[camelKey] = splitValue(val, splitter);
      } else {
        obj[camelKey] = val;
      }
    }
    records.push(obj);
  }

  //  discard records with status explicitly set to inactive
  return records.filter((record) =>
    typeof record.status === 'string' ? record.status.toUpperCase() !== 'INACTIVE' : true
  );
}

// some fields are multiselects. This parses those answers into a string[] (these are the ParseOptions supplied to options in parseCsvFile and parseCsvTextCamelCase)
function splitValue(val: string, splitter: ListSplitter): string[] {
  const normalized = val
    .replace(/\\r\\n/g, '\n') // literal \r\n to newline
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');

  let parts: string[];
  if (splitter === 'newline') {
    parts = normalized.split('\n');
  } else if (splitter === 'comma') {
    parts = normalized.split(',');
  } else if (splitter instanceof RegExp) {
    parts = normalized.split(splitter);
  } else {
    parts = splitter(normalized);
  }

  return parts
    .map((s) => s.trim().replace(/^"+|"+$/g, '')) // trim and strip stray quotes
    .filter(Boolean);
}

// Create safe camelCase keys from headers
function toCamelCase(header: string): string {
  // Remove diacritics, pull out alphanumerics, split on anything else
  const cleaned = header.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');

  const parts = cleaned.match(/[A-Za-z0-9]+/g) ?? [];
  if (parts.length === 0) return '';

  const lower = parts.map((p) => p.toLowerCase());
  const camel = lower[0] + lower.slice(1).map(capitalize).join('');

  // Ensure it starts with a letter or underscore to be a valid JS identifier
  return /^[A-Za-z_]/.test(camel) ? camel : `col${camel}`;
}

function capitalize(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s;
}

// Ensure unique header keys by suffixing duplicates (e.g., "status", "status2")
function makeUnique(keys: string[]): string[] {
  const seen = new Map<string, number>();
  return keys.map((k) => {
    const count = seen.get(k) ?? 0;
    seen.set(k, count + 1);
    return count === 0 ? k : `${k}${count + 1}`;
  });
}

/**
 * CSV Parser
 * walks the input string character by character, builds cells (fields) and rows,
 * and correctly handles commas, newlines, and quotes (including escaped quotes).
 * Returns an array of rows, where each row is an array of strings.
 */
function parseCsv(csvText: string): string[][] {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let field = '';
  // are we inside a quoted field right now
  let inQuotes = false;
  let i = 0;

  while (i < csvText.length) {
    const char = csvText[i];

    // When inQuotes is true (current char is inside a quoted field):
    if (inQuotes) {
      // If the current char is a double quote:
      if (char === '"') {
        const next = csvText[i + 1];
        // It peeks at the next char.
        // If the next char is also a double quote, that represents an escaped quote inside a quoted field.
        //  It appends a single " to field and advances by two characters.
        if (next === '"') {
          field += '"';
          i += 2;
          // Otherwise, that quote closes the quoted field. It sets inQuotes = false and advances by one character.
          // The field isn’t pushed yet; the delimiter (comma) or newline will end the field/row in the outer logic.
        } else {
          inQuotes = false;
          i += 1;
        }
      } else {
        field += char;
        i += 1;
      }
      // When inQuotes is false (normal mode):
    } else {
      // If the char is a double quote, that starts a quoted field. Set inQuotes = true and advance.
      if (char === '"') {
        inQuotes = true;
        i += 1;
        // If the char is a comma, that ends the current field.
        // Push field to currentRow, reset field to "", advance.
      } else if (char === ',') {
        currentRow.push(field);
        field = '';
        i += 1;
        // If the char is a carriage return '\r', that ends the current row.
        // Push the field, push the row, reset.
        // Then, if the next char is '\n', consume it too (handling Windows CRLF).
      } else if (char === '\r') {
        currentRow.push(field);
        field = '';
        rows.push(currentRow);
        currentRow = [];
        i += 1;
        if (csvText[i] === '\n') i += 1;
        // If the char is a line feed '\n', that also ends the current row (Unix LF).
        // Push field, push row, reset, advance.
      } else if (char === '\n') {
        currentRow.push(field);
        field = '';
        rows.push(currentRow);
        currentRow = [];
        i += 1;
        // Otherwise, it’s a regular character in an unquoted field. Append it to field and advance.
      } else {
        field += char;
        i += 1;
      }
    }
  }
  currentRow.push(field);
  rows.push(currentRow);
  return rows.filter((row) => row.some((cell) => cell.length > 0));
}

export function checkParsedAmbassadorsValidity(csvData: UpsertAmbassadorRequest[]): boolean {
  const ambassadorToCheck = csvData[0];

  return (
    ambassadorToCheck &&
    typeof ambassadorToCheck.alias === 'string' &&
    Array.isArray(ambassadorToCheck.ergName) &&
    Array.isArray(ambassadorToCheck.heardAbout) &&
    Array.isArray(ambassadorToCheck.representationTypes) &&
    Array.isArray(ambassadorToCheck.teamLocation) &&
    typeof ambassadorToCheck.teamName === 'string'
  );
}

export function csvItemsToAmbassadorRequest(
  csvData: Record<string, string | string[]>[]
): UpsertAmbassadorRequest[] {
  // if the created date in the CSV is blank, we default to 01/01/2024 (https://issues.amazon.com/issues/VOLUNTEERCENTER-353)
  const fallbackCreationDate = new Date('01/01/2024');
  const validRepresentationValues = Object.values(RepresentationTypes);

  return csvData.map((row) => {
    const repTypes = (row.representationType as string[])
      .map((rt) => {
        const sanitizedValue = rt.trim().toUpperCase() as RepresentationTypes;

        return validRepresentationValues.includes(sanitizedValue) ? sanitizedValue : undefined;
      })
      .filter(Boolean);

    const newAmbassador = {
      alias:
        row.ambassadorAlias ||
        ((row.ambassadorEmail || row.ambassador || '') as string).split('@')[0],
      representationTypes: repTypes, // this has to be mapped better
      heardAbout: row.whereDidYouHearAboutTheProgam,
      teamLocation: row.teamLocation,
      teamName: row.teamName,
      onboardedDate: row.created ? new Date(row.created as string) : fallbackCreationDate,
      ergName: [row.ergName || ''],
    } as UpsertAmbassadorRequest;

    return newAmbassador;
  });
}
